// src/products/dataroom/services/permissionService.js
// Focused service for Dataroom permission management

import { supabase } from '../../../config';

class PermissionService {
  /**
   * Get user permissions for a project
   */
  async getUserPermissions(userId, projectId) {
    try {
      const { data, error } = await supabase
        .from('dataroom_users')
        .select('role, status')
        .eq('user_id', userId)
        .eq('project_id', projectId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      // Blocked users have no permissions
      if (data?.status === 'blocked') {
        return { 
          data: { role: data.role || null, status: 'blocked', blocked: true }, 
          error: null 
        };
      }

      return { 
        data: data || { role: null }, 
        error: null 
      };
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      return { data: null, error };
    }
  }

  /**
   * Get project users
   */
  async getProjectUsers(projectId) {
    try {
      // First get dataroom users
      const { data: dataroomUsers, error: dataroomError } = await supabase
        .from('dataroom_users')
        .select('user_id, email, role, status, invited_at, last_active, metadata')
        .eq('project_id', projectId)
        .order('invited_at', { ascending: false });

      if (dataroomError) throw dataroomError;

      if (!dataroomUsers || dataroomUsers.length === 0) {
        return { data: [], error: null };
      }

      // Then get user profile information for each user (only for those with user_id)
      const userIds = dataroomUsers.map(u => u.user_id).filter(Boolean); // Filter out nulls
      
      let profiles = [];
      if (userIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('user_profiles')
          .select('id, email, first_name, last_name')
          .in('id', userIds);

        if (profilesError) {
          console.warn('Error fetching user profiles:', profilesError);
        } else {
          profiles = profilesData || [];
        }
      }

      // Merge the data
      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);
      const mergedData = dataroomUsers.map(u => {
        const profile = profileMap.get(u.user_id);
        const metadata = u.metadata || {};
        
        // Use email from dataroom_users first, then profile, then user_id fallback
        const email = u.email || profile?.email || u.user_id;
        
        // Use name from metadata (wizard), then profile, then email
        const fullName = metadata.first_name && metadata.last_name
          ? `${metadata.first_name} ${metadata.last_name}`
          : metadata.first_name
            ? metadata.first_name
            : (profile?.first_name && profile?.last_name)
              ? `${profile.first_name} ${profile.last_name}`
              : profile?.first_name || profile?.last_name || email.split('@')[0];
        
        return {
          ...u,
          email: email,
          fullName: fullName,
          profile: profile || null,
          metadata: metadata
        };
      });

      return { data: mergedData, error: null };
    } catch (error) {
      console.error('Error fetching project users:', error);
      return { data: null, error };
    }
  }

  /**
   * Add user to project
   */
  async addUserToProject(projectId, userId, role, permissions = {}) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('dataroom_users')
        .insert({
          project_id: projectId,
          user_id: userId,
          role: role,
          permissions: permissions,
          invited_by: user.id,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      // Log activity
      try {
        await this.logActivity(projectId, 'user_added', `User added to project with role: ${role}`);
      } catch (activityError) {
        console.warn('Failed to log activity:', activityError);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error adding user to project:', error);
      return { data: null, error };
    }
  }

  /**
   * Update user permissions
   */
  async updateUserPermissions(projectId, userId, role, permissions) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('dataroom_users')
        .update({
          role: role,
          permissions: permissions,
          updated_at: new Date().toISOString()
        })
        .eq('project_id', projectId)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;

      // Log activity
      try {
        await this.logActivity(projectId, 'permissions_updated', `User permissions updated`);
      } catch (activityError) {
        console.warn('Failed to log activity:', activityError);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating user permissions:', error);
      return { data: null, error };
    }
  }

  /**
   * Remove user from project
   */
  async removeUserFromProject(projectId, userId) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('dataroom_users')
        .delete()
        .eq('project_id', projectId)
        .eq('user_id', userId);

      if (error) throw error;

      // Log activity
      try {
        await this.logActivity(projectId, 'user_removed', `User removed from project`);
      } catch (activityError) {
        console.warn('Failed to log activity:', activityError);
      }

      return { data: { success: true }, error: null };
    } catch (error) {
      console.error('Error removing user from project:', error);
      return { data: null, error };
    }
  }

  /**
   * Get item permissions (Teams-based)
   */
  async getItemPermissions(itemId) {
    try {
      const { data, error } = await supabase
        .from('dataroom_file_permissions')
        .select(`
          *,
          dataroom_teams (
            id,
            name,
            dataroom_parties (
              id,
              name
            )
          )
        `)
        .eq('dataroom_item_id', itemId);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching item permissions:', error);
      return { data: null, error };
    }
  }

  /**
   * Update item permissions (Teams-based)
   * @param {number} itemId - The dataroom item ID
   * @param {Array} permissions - Array of { teamId, permissionType } objects
   */
  async updateItemPermissions(itemId, permissions) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Delete existing permissions
      const { error: deleteError } = await supabase
        .from('dataroom_file_permissions')
        .delete()
        .eq('dataroom_item_id', itemId);

      if (deleteError) throw deleteError;

      // Insert new permissions
      if (permissions.length > 0) {
        const permissionData = permissions.map(perm => ({
          dataroom_item_id: itemId,
          team_id: perm.teamId,
          permission_type: perm.permissionType
        }));

        const { error: insertError } = await supabase
          .from('dataroom_file_permissions')
          .insert(permissionData);

        if (insertError) throw insertError;
      }

      // Log activity
      try {
        const { data: item } = await supabase
          .from('dataroom_items')
          .select('project_id, name')
          .eq('id', itemId)
          .single();

        if (item) {
          await this.logActivity(item.project_id, 'permissions_changed', 
            `Permissions updated for "${item.name}"`);
        }
      } catch (activityError) {
        console.warn('Failed to log activity:', activityError);
      }

      return { data: { success: true }, error: null };
    } catch (error) {
      console.error('Error updating item permissions:', error);
      return { data: null, error };
    }
  }

  /**
   * Check if user has permission for item (Teams-based)
   * Permission flow: Party → Team → User
   * Users inherit permissions from their team membership within parties
   */
  async checkItemPermission(userId, itemId, permissionType) {
    try {
      // Get item and its project
      const { data: item } = await supabase
        .from('dataroom_items')
        .select('project_id')
        .eq('id', itemId)
        .single();

      if (!item) return { data: false, error: null };

      // Check user's project role and status (creator/support have all permissions)
      const { data: userRole } = await supabase
        .from('dataroom_users')
        .select('role, status')
        .eq('user_id', userId)
        .eq('project_id', item.project_id)
        .single();

      // Blocked users have no access
      if (userRole?.status === 'blocked') {
        return { data: false, error: null };
      }

      // Creator and support roles have all permissions
      if (userRole?.role === 'creator' || userRole?.role === 'support') {
        return { data: true, error: null };
      }

      // Get file permissions for this item (Teams-based)
      const { data: filePermissions, error: permError } = await supabase
        .from('dataroom_file_permissions')
        .select(`
          team_id,
          permission_type,
          dataroom_teams (
            id,
            party_id,
            dataroom_parties (
              id,
              project_id
            )
          )
        `)
        .eq('dataroom_item_id', itemId)
        .eq('permission_type', permissionType);

      if (permError) throw permError;

      if (!filePermissions || filePermissions.length === 0) {
        return { data: false, error: null };
      }

      // Filter permissions to only teams in this project
      const teamIdsWithPermission = filePermissions
        .filter(fp => fp.dataroom_teams?.dataroom_parties?.project_id === item.project_id)
        .map(fp => fp.team_id);

      if (teamIdsWithPermission.length === 0) {
        return { data: false, error: null };
      }

      // Check if user belongs to any of these teams in this project
      const { data: userTeams, error: teamsError } = await supabase
        .from('dataroom_user_teams')
        .select(`
          team_id,
          dataroom_teams (
            id,
            party_id,
            dataroom_parties (
              id,
              project_id
            )
          )
        `)
        .eq('user_id', userId)
        .eq('project_id', item.project_id)
        .in('team_id', teamIdsWithPermission);

      if (teamsError) throw teamsError;

      // Verify teams are in the correct project (double-check)
      const validUserTeams = userTeams?.filter(ut => 
        ut.dataroom_teams?.dataroom_parties?.project_id === item.project_id
      ) || [];

      // User has permission if they belong to at least one team with permission
      const hasPermission = validUserTeams.length > 0;

      return { data: hasPermission, error: null };
    } catch (error) {
      console.error('Error checking item permission:', error);
      return { data: false, error };
    }
  }

  /**
   * Get project teams (replaces getProjectGroups)
   * Returns all teams across all parties in a project
   */
  async getProjectTeams(projectId) {
    try {
      const { data, error } = await supabase
        .from('dataroom_teams')
        .select(`
          *,
          dataroom_parties!inner (
            id,
            name,
            project_id
          )
        `)
        .eq('dataroom_parties.project_id', projectId)
        .order('name');

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching project teams:', error);
      return { data: null, error };
    }
  }

  /**
   * @deprecated Use getProjectTeams instead
   * Get project groups - DEPRECATED, use getProjectTeams
   */
  async getProjectGroups(projectId) {
    console.warn('getProjectGroups is deprecated. Use getProjectTeams instead.');
    return this.getProjectTeams(projectId);
  }

  /**
   * Log activity for permission operations
   */
  async logActivity(projectId, action, description, metadata = {}) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('dataroom_logs')
        .insert({
          project_id: projectId,
          user_id: user.id,
          action: action,
          details: {
            description: description,
            ...metadata
          }
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error logging activity:', error);
      throw error;
    }
  }
}

const permissionService = new PermissionService();
export default permissionService;
